/**
 * AdminLeads Component
 * Lead management dashboard with search, filtering, and export
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import useAdminAuth from '../../hooks/useAdminAuth';
import AdminLayout from './AdminLayout';
import { API_BASE_URL } from '../../config/api';

interface Lead {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  message: string | null;
  source: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  created_at: string;
  updated_at: string;
  converted: boolean;
}

interface LeadsPagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

type LimitOption = 25 | 50 | 100 | 200;

const AdminLeads: React.FC = () => {
  const { isAdmin, isLoading: authLoading } = useAdminAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [leads, setLeads] = useState<Lead[]>([]);
  const [pagination, setPagination] = useState<LeadsPagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [filter, setFilter] = useState(searchParams.get('filter') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [limit] = useState<LimitOption>(25);
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    converted: 0,
    thisWeek: 0,
    thisMonth: 0
  });

  const fetchLeads = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('account-token');
      
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', limit.toString());
      if (search) params.set('search', search);
      if (filter) params.set('filter', filter);
      
      const response = await fetch(`${API_BASE_URL}/api/admin/leads?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch leads');
      
      const data = await response.json();
      setLeads(data.leads);
      setPagination(data.pagination);
      setStats(data.stats);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
      setError('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, limit, search, filter]);

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchLeads();
    }
  }, [authLoading, isAdmin, fetchLeads]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (filter) params.set('filter', filter);
    if (page > 1) params.set('page', page.toString());
    setSearchParams(params);
  }, [search, filter, page, setSearchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchLeads();
  };

  const handleMarkConverted = async (leadId: number) => {
    try {
      const token = localStorage.getItem('account-token');
      await fetch(`${API_BASE_URL}/api/admin/leads/${leadId}/converted`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ converted: true })
      });
      fetchLeads();
    } catch (err) {
      console.error('Failed to mark as converted:', err);
    }
  };

  const handleExportCSV = () => {
    const headers = ['Email', 'First Name', 'Last Name', 'Source', 'UTM Source', 'UTM Medium', 'UTM Campaign', 'Message', 'Created', 'Converted'];
    const csvContent = [
      headers.join(','),
      ...leads.map(lead => [
        `"${lead.email}"`,
        `"${lead.first_name}"`,
        `"${lead.last_name}"`,
        `"${lead.source || ''}"`,
        `"${lead.utm_source || ''}"`,
        `"${lead.utm_medium || ''}"`,
        `"${lead.utm_campaign || ''}"`,
        `"${(lead.message || '').replace(/"/g, '""')}"`,
        `"${new Date(lead.created_at).toLocaleDateString()}"`,
        lead.converted ? 'Yes' : 'No'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (authLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-cyan-700 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-red-600">Access Denied</h2>
          <p className="text-stone-600 mt-2">You don't have permission to view this page.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-stone-800">Lead Management</h1>
            <p className="text-stone-600">Manage captured leads from signup forms</p>
          </div>
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export CSV
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-stone-500">Total Leads</p>
            <p className="text-2xl font-bold text-stone-800">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-stone-500">Converted</p>
            <p className="text-2xl font-bold text-green-600">{stats.converted}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-stone-500">This Week</p>
            <p className="text-2xl font-bold text-cyan-700">{stats.thisWeek}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-stone-500">This Month</p>
            <p className="text-2xl font-bold text-purple-600">{stats.thisMonth}</p>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow p-4">
          <form onSubmit={handleSearch} className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => { setFilter(e.target.value); setPage(1); }}
              className="px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">All Sources</option>
              <option value="google">Google Search</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="linkedin">LinkedIn</option>
              <option value="twitter">Twitter/X</option>
              <option value="friend">Friend/Family</option>
              <option value="advisor">Financial Advisor</option>
              <option value="attorney">Attorney</option>
              <option value="podcast">Podcast</option>
              <option value="blog">Blog/Article</option>
              <option value="advertisement">Online Ad</option>
              <option value="event">Event/Conference</option>
              <option value="other">Other</option>
            </select>
            <select
              value={filter === 'converted' ? 'converted' : filter === 'not_converted' ? 'not_converted' : ''}
              onChange={(e) => { setFilter(e.target.value); setPage(1); }}
              className="px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">All Status</option>
              <option value="converted">Converted</option>
              <option value="not_converted">Not Converted</option>
            </select>
            <button
              type="submit"
              className="px-6 py-2 bg-cyan-700 text-white rounded-lg hover:bg-cyan-800"
            >
              Search
            </button>
          </form>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Leads Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-cyan-700 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              No leads found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-stone-200">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">UTM</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">Message</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-stone-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-stone-200">
                  {leads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-stone-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="font-medium text-stone-900">{lead.first_name} {lead.last_name}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <a href={`mailto:${lead.email}`} className="text-cyan-700 hover:underline">
                          {lead.email}
                        </a>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs rounded-full bg-stone-100 text-stone-700">
                          {lead.source || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-stone-500">
                        {lead.utm_source && (
                          <div className="text-xs">
                            {lead.utm_source}
                            {lead.utm_medium && ` / ${lead.utm_medium}`}
                          </div>
                        )}
                        {!lead.utm_source && '-'}
                      </td>
                      <td className="px-4 py-4 max-w-xs">
                        <div className="text-sm text-stone-600 truncate" title={lead.message || ''}>
                          {lead.message || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-stone-500">
                        {formatDate(lead.created_at)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {lead.converted ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
                            Converted
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        {!lead.converted && (
                          <button
                            onClick={() => handleMarkConverted(lead.id)}
                            className="text-sm text-cyan-700 hover:text-cyan-800"
                          >
                            Mark Converted
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between bg-white rounded-lg shadow px-4 py-3">
            <div className="text-sm text-stone-600">
              Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, pagination.total)} of {pagination.total} leads
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-stone-200 rounded hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-stone-600">
                Page {page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                disabled={page === pagination.totalPages}
                className="px-3 py-1 border border-stone-200 rounded hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminLeads;

